<template>
  <v-navigation-drawer
    :value="value"
    app
    right
    temporary
    width="760"
    @input="$emit('input', $event)"
  >
    <v-toolbar flat dense>
      <v-toolbar-title class="text-subtitle-1 font-weight-bold">
        {{ $t('classlink.scope.title') }}
      </v-toolbar-title>
      <v-spacer />
      <v-btn icon @click="close">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>

    <div class="px-4 pb-6">
      <p class="text-caption grey--text mb-2">
        {{ $t('classlink.scope.subtitle') }}
      </p>

      <v-progress-linear v-if="loading" indeterminate color="deep-purple" />

      <v-alert v-if="!loading && unavailable" type="info" dense text class="mb-3">
        {{ $t('classlink.scope.unavailable') }}
      </v-alert>

      <v-alert v-else-if="!loading && loadFailed" type="error" dense text class="mb-3">
        {{ $t('classlink.scope.load_error') }}
      </v-alert>

      <template v-if="!loading && !unavailable && !loadFailed">
        <!-- Con la fuente degradada no hay nombres reales y el PUT reemplaza
             la selección entera, así que ni escuelas ni grados se pueden
             editar: lo único posible es quitar el filtro. -->
        <v-alert v-if="readOnly" type="warning" dense text class="mb-3">
          {{ $t('classlink.scope.stale_source') }}
        </v-alert>

        <!-- Escuelas -->
        <div class="text-subtitle-2 font-weight-bold mb-1">
          {{ $t('classlink.scope.schools_title') }}
        </div>
        <p class="text-caption grey--text mb-2">{{ schoolsStateText }}</p>

        <template v-if="readOnly">
          <p
            v-if="savedSchools.length === 0 && !syncsAllOrgs"
            class="text-caption grey--text"
          >
            {{ $t('classlink.scope.schools_empty') }}
          </p>
          <v-chip
            v-for="org in savedSchools"
            :key="org.sourcedId"
            small
            outlined
            class="mr-1 mb-1"
          >
            {{ org.name }}
          </v-chip>
        </template>

        <template v-else>
          <p v-if="allSchools.length === 0" class="text-caption grey--text">
            {{ $t('classlink.scope.schools_empty') }}
          </p>

          <template v-else>
            <v-text-field
              v-model="search"
              :label="$t('classlink.scope.schools_search')"
              prepend-inner-icon="mdi-magnify"
              dense
              outlined
              hide-details
              clearable
              class="mb-2"
            />

            <div class="d-flex align-center mb-1">
              <v-btn text x-small color="deep-purple" @click="selectAllVisible">
                {{ $t('classlink.scope.select_all') }}
              </v-btn>
              <v-btn text x-small @click="selectNoneVisible">
                {{ $t('classlink.scope.select_none') }}
              </v-btn>
              <v-spacer />
              <span class="text-caption grey--text">
                {{ $t('classlink.scope.selected_count', {
                  n: count(selectedSchools),
                  total: count(allSchools.length),
                }) }}
              </span>
            </div>

            <p
              v-if="visibleSchools.length === 0"
              class="text-caption grey--text"
            >
              {{ $t('classlink.scope.schools_no_results') }}
            </p>

            <v-checkbox
              v-for="org in visibleSchools"
              :key="org.sourcedId"
              v-model="selectedOrgIds"
              :value="org.sourcedId"
              dense
              hide-details
              class="mt-0 pt-0"
            >
              <template #label>
                <span>
                  {{ org.name }}
                  <span v-if="org.synced" class="text-caption grey--text ml-1">
                    {{ schoolSyncedText(org.synced) }}
                  </span>
                </span>
              </template>
            </v-checkbox>

            <p class="text-caption grey--text mt-2">
              {{ $t('classlink.scope.schools_hint') }}
            </p>
          </template>
        </template>

        <v-divider class="my-4" />

        <!-- Grados -->
        <div class="text-subtitle-2 font-weight-bold mb-1">
          {{ $t('classlink.scope.grades_title') }}
        </div>
        <p class="text-caption grey--text mb-2">{{ gradesStateText }}</p>

        <p v-if="sortedGrades.length === 0" class="text-caption grey--text">
          {{ $t('classlink.scope.grades_empty') }}
        </p>

        <template v-else-if="readOnly">
          <v-chip
            v-for="code in savedGrades"
            :key="code"
            small
            outlined
            class="mr-1 mb-1"
          >
            {{ gradeLabel(code) }}
          </v-chip>
        </template>

        <template v-else>
          <v-chip
            v-for="grade in sortedGrades"
            :key="grade.code"
            small
            filter
            outlined
            :input-value="isGradeSelected(grade.code)"
            class="mr-1 mb-1"
            @click="toggleGrade(grade.code)"
          >
            {{ gradeLabel(grade.code) }}
            <span v-if="grade.studentsCount !== undefined" class="grey--text ml-1">
              ({{ count(grade.studentsCount) }})
            </span>
          </v-chip>

          <p class="text-caption grey--text mt-2">
            {{ $t('classlink.scope.grades_hint') }}
          </p>
        </template>

        <v-divider class="my-4" />

        <div class="d-flex align-center">
          <v-btn
            text
            small
            :disabled="saving || !hasSavedFilter"
            @click="clearFilter"
          >
            {{ $t('classlink.scope.clear') }}
          </v-btn>
          <v-spacer />
          <v-btn text small :disabled="saving" @click="close">
            {{ $t('classlink.scope.cancel') }}
          </v-btn>
          <v-btn
            color="deep-purple"
            dark
            small
            class="ml-2"
            :loading="saving"
            :disabled="saving || readOnly"
            @click="attemptSave"
          >
            {{ $t('classlink.scope.save') }}
          </v-btn>
        </div>
      </template>
    </div>

    <v-dialog v-model="confirmOpen" max-width="520">
      <v-card>
        <v-card-title class="text-h6">
          {{ $t('classlink.scope.confirm_title') }}
        </v-card-title>
        <v-card-text>
          <p>{{ confirmText }}</p>

          <!--
            El conteo de REGISTROS, no de escuelas: la diferencia entre "21
            escuelas" y "4.400 alumnos" es la que hace que alguien frene.
            Sale del dry-run, que no escribe nada.
          -->
          <v-progress-linear
            v-if="previewLoading"
            indeterminate
            color="deep-purple"
            class="mb-3"
          />
          <v-alert
            v-else-if="previewError"
            type="warning"
            text
            dense
            class="mb-3"
          >
            {{ $t('classlink.scope.preview_error') }}
          </v-alert>
          <v-alert
            v-else-if="preview && previewTotal > 0"
            type="warning"
            text
            dense
            class="mb-3"
          >
            <div>{{ $t('classlink.scope.preview_deactivate_title') }}</div>
            <ul class="mt-1 mb-0">
              <li v-if="preview.deactivate.users">
                {{ $tc('classlink.scope.preview_users', preview.deactivate.users) }}
              </li>
              <li v-if="preview.deactivate.courses">
                {{ $tc('classlink.scope.preview_courses', preview.deactivate.courses) }}
              </li>
              <li v-if="preview.deactivate.enrollments">
                {{ $tc('classlink.scope.preview_enrollments', preview.deactivate.enrollments) }}
              </li>
            </ul>
          </v-alert>
          <v-alert
            v-else-if="preview && reactivateTotal > 0"
            type="info"
            text
            dense
            class="mb-3"
          >
            {{ $tc('classlink.scope.preview_reactivate', reactivateTotal) }}
          </v-alert>

          <p class="text-caption grey--text mb-0">
            {{ $t('classlink.scope.confirm_note') }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="saving" @click="confirmOpen = false">
            {{ $t('classlink.scope.confirm_cancel') }}
          </v-btn>
          <v-btn
            color="deep-purple"
            dark
            :loading="saving"
            :disabled="saving"
            @click="confirmSave"
          >
            {{ $t('classlink.scope.confirm_accept') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-navigation-drawer>
</template>

<script>
import rosterTenantsService from '@/services/roster-tenants.service';
import {
  apiErrorMessage,
  apiStatus,
  countsTotal,
  filterScopeOrgs,
  formatCount,
  gradeLabelKey,
  schoolsOf,
  scopeConfirmation,
  selectedSchoolCount,
  sortGrades,
  supportedGrades,
  UNSUPPORTED_GRADE_CODES,
} from '@/utils/classlinkTenants';
import snackbarMixin from '@/mixins/snackbar';

export default {
  name: 'ClassLinkScopeDrawer',
  mixins: [snackbarMixin],
  props: {
    value: { type: Boolean, default: false },
    tenant: { type: Object, default: null },
  },
  data: () => ({
    // Roster en vivo (`/orgs`). Vacío si ClassLink no respondió.
    orgs: [],
    source: 'live',
    syncsAllOrgs: true,
    // Lo guardado (`/scope`). Es lectura pura de base, así que sobrevive a que
    // ClassLink esté caído, y es la única fuente de `availableGrades`.
    savedOrgs: [],
    savedOrgIds: [],
    savedGrades: [],
    syncsAllGrades: true,
    availableGrades: [],
    // Selección en edición.
    selectedOrgIds: [],
    selectedGrades: [],
    search: '',
    loading: false,
    // `/orgs` falló: se puede seguir mostrando lo guardado en lectura, igual
    // que con `source: 'persisted'`.
    orgsFailed: false,
    // `/scope` falló: sin eso no hay nada que mostrar.
    loadFailed: false,
    // 404 = el backend de KB-950 todavía no está desplegado en este ambiente
    // (mismo caso que `unavailable` en la vista de conexiones).
    unavailable: false,
    saving: false,
    confirmOpen: false,
    confirmMessage: null,
    preview: null,
    previewLoading: false,
    previewError: false,
  }),
  computed: {
    /**
     * Sin roster en vivo no se puede elegir: con `source: 'persisted'` el
     * `name` de cada org ES el `sourcedId`, y como el PUT reemplaza la
     * selección entera, guardar sólo los grados reenviaría `org_ids` y el
     * backend respondería 503. Lo único posible acá es quitar el filtro.
     */
    readOnly() {
      return this.orgsFailed || this.source === 'persisted';
    },
    allSchools() {
      return schoolsOf(this.orgs);
    },
    visibleSchools() {
      return filterScopeOrgs(this.allSchools, this.search);
    },
    savedSchools() {
      return schoolsOf(this.savedOrgs);
    },
    selectedSchools() {
      return selectedSchoolCount(this.orgs, this.selectedOrgIds);
    },
    sortedGrades() {
      return sortGrades(
        this.availableGrades.filter((g) => !UNSUPPORTED_GRADE_CODES.includes(g.code)),
      );
    },
    // Se leen los dos flags del backend y no `savedGrades.length`: son su
    // propia noción de "hay filtro", y derivarla acá podría divergir.
    hasSavedFilter() {
      return !this.syncsAllOrgs || !this.syncsAllGrades;
    },
    schoolsStateText() {
      if (this.syncsAllOrgs) {
        return this.$t('classlink.scope.schools_all');
      }
      // Sin roster en vivo no se sabe cuántas escuelas tiene el distrito: el
      // `/scope` sólo devuelve las guardadas. Ahí el texto va sin total, en
      // vez de mostrar un "N de N" que no significa nada.
      if (this.readOnly) {
        const saved = this.savedSchools.length;
        return this.$tc('classlink.scope.schools_saved_readonly', saved, {
          n: this.count(saved),
        });
      }
      const n = selectedSchoolCount(this.orgs, this.savedOrgIds);
      return this.$tc('classlink.scope.schools_saved', n, {
        n: this.count(n),
        total: this.count(this.allSchools.length),
      });
    },
    gradesStateText() {
      if (this.syncsAllGrades) {
        return this.$t('classlink.scope.grades_all');
      }
      return this.$tc('classlink.scope.grades_saved', this.savedGrades.length, {
        n: this.count(this.savedGrades.length),
      });
    },
    previewTotal() {
      return countsTotal(this.preview && this.preview.deactivate);
    },
    reactivateTotal() {
      return countsTotal(this.preview && this.preview.reactivate);
    },
    confirmText() {
      if (!this.confirmMessage) return '';
      const { excluded, remaining } = this.confirmMessage.params;
      return this.$tc(this.confirmMessage.key, excluded, {
        excluded: this.count(excluded),
        remaining: this.count(remaining),
      });
    },
  },
  watch: {
    value(open) {
      if (open) this.load();
    },
    // Mismo motivo que en `ClassLinkUnlinkedOrgsDrawer`: el drawer se abre
    // desde cualquier fila, así que si cambia de conexión mientras está
    // abierto, `value` no cambia y quedaría a la vista el alcance de la
    // conexión anterior.
    tenant(next, prev) {
      if (this.value && next && (!prev || next.id !== prev.id)) this.load();
    },
  },
  methods: {
    count(value) {
      return formatCount(value || 0, this.$i18n.locale);
    },
    // `$te ? $t : código crudo`, igual que `outcomeLabel` en la vista: un
    // código de grado sin etiqueta todavía se ve como viene y no como una
    // clave de i18n sin traducir.
    gradeLabel(code) {
      const key = gradeLabelKey(code);
      return this.$te(key) ? this.$t(key) : code;
    },
    // KB-970: desglose de gente ya sincronizada, al lado del nombre de la
    // escuela en el checklist. Solo se llama cuando `org.synced` existe.
    schoolSyncedText(synced) {
      return this.$t('classlink.scope.schools_synced_breakdown', {
        students: this.count(synced.studentsCount),
        teachers: this.count(synced.teachersCount),
        staff: this.count(synced.staffCount),
      });
    },
    isGradeSelected(code) {
      return this.selectedGrades.indexOf(code) !== -1;
    },
    toggleGrade(code) {
      const index = this.selectedGrades.indexOf(code);
      if (index === -1) {
        this.selectedGrades = this.selectedGrades.concat([code]);
      } else {
        this.selectedGrades = this.selectedGrades.filter((item) => item !== code);
      }
    },
    // Actúan sobre lo que se ve, no sobre todo: con la búsqueda activa,
    // "seleccionar todas" sobre las 25 escuelas del distrito sería justo lo
    // contrario de lo que quiere quien acaba de filtrar por nombre.
    selectAllVisible() {
      const visible = this.visibleSchools.map((org) => org.sourcedId);
      const missing = visible.filter((id) => this.selectedOrgIds.indexOf(id) === -1);
      this.selectedOrgIds = this.selectedOrgIds.concat(missing);
    },
    selectNoneVisible() {
      const visible = this.visibleSchools.map((org) => org.sourcedId);
      this.selectedOrgIds = this.selectedOrgIds.filter((id) => visible.indexOf(id) === -1);
    },
    close() {
      this.$emit('input', false);
    },
    reset() {
      this.orgs = [];
      this.source = 'live';
      this.syncsAllOrgs = true;
      this.savedOrgs = [];
      this.savedOrgIds = [];
      this.savedGrades = [];
      this.syncsAllGrades = true;
      this.availableGrades = [];
      this.selectedOrgIds = [];
      this.selectedGrades = [];
      this.search = '';
      this.orgsFailed = false;
      this.loadFailed = false;
      this.unavailable = false;
    },
    /**
     * Las dos cargas se piden juntas pero cada una atrapa su propio error:
     * `/orgs` consulta ClassLink y puede caerse, `/scope` es lectura de base y
     * no. Con un `Promise.all` crudo, que se cayera la primera dejaría en
     * blanco un drawer que podía mostrar perfectamente lo guardado.
     */
    async load() {
      if (!this.tenant) return;
      // Mismo guard que el drawer de orgs sin vincular: si se reabre para otra
      // conexión antes de que estas requests resuelvan, la respuesta vieja no
      // debe pisar el alcance de la conexión nueva.
      const requestedId = this.tenant.id;
      this.loading = true;
      this.reset();

      const [orgsResult, scopeResult] = await Promise.all([
        rosterTenantsService.availableOrgs(requestedId).then(
          (data) => ({ data }),
          (error) => ({ error }),
        ),
        rosterTenantsService.getScope(requestedId).then(
          (data) => ({ data }),
          (error) => ({ error }),
        ),
      ]);

      if (!this.tenant || this.tenant.id !== requestedId) return;

      if (scopeResult.error) {
        if (apiStatus(scopeResult.error) === 404) {
          this.unavailable = true;
        } else {
          this.loadFailed = true;
          this.snackbarError(apiErrorMessage(
            scopeResult.error,
            this.$t('classlink.scope.load_error'),
          ));
        }
        this.loading = false;
        return;
      }

      const scope = scopeResult.data;
      this.savedOrgs = scope.orgs || [];
      this.savedOrgIds = scope.orgIds || [];
      this.savedGrades = supportedGrades(scope.grades || []);
      this.syncsAllGrades = scope.syncsAllGrades;
      this.availableGrades = scope.availableGrades || [];
      this.syncsAllOrgs = scope.syncsAllOrgs;
      this.selectedOrgIds = [...this.savedOrgIds];
      this.selectedGrades = [...this.savedGrades];

      if (orgsResult.error) {
        this.orgsFailed = true;
      } else {
        this.orgs = orgsResult.data.orgs || [];
        this.source = orgsResult.data.source;
        this.syncsAllOrgs = orgsResult.data.syncsAllOrgs;
      }

      this.loading = false;
    },
    /**
     * Guardar con cero escuelas tildadas quita el filtro y hace que vuelva a
     * entrar el distrito entero, así que no se confirma: el aviso es sólo para
     * el caso destructivo, que es achicar la selección.
     */
    attemptSave() {
      const message = scopeConfirmation({
        orgs: this.orgs,
        syncsAllOrgs: this.syncsAllOrgs,
        currentOrgIds: this.savedOrgIds,
        nextOrgIds: this.selectedOrgIds,
      });

      if (!message) {
        this.save(this.selectedOrgIds, this.selectedGrades);
        return;
      }

      this.confirmMessage = message;
      this.confirmOpen = true;
      this.loadPreview();
    },
    /**
     * Dry-run del guardado. Un fallo acá NO bloquea: se avisa que no se pudo
     * calcular y la confirmación sigue disponible. Bloquear dejaría al
     * operador sin poder guardar por un problema de un número informativo.
     */
    async loadPreview() {
      if (!this.tenant) return;
      this.preview = null;
      this.previewError = false;
      this.previewLoading = true;
      try {
        this.preview = await rosterTenantsService.previewScope(this.tenant.id, {
          orgIds: this.selectedOrgIds,
          grades: this.selectedGrades,
        });
      } catch (error) {
        this.previewError = true;
      } finally {
        this.previewLoading = false;
      }
    },
    confirmSave() {
      this.save(this.selectedOrgIds, this.selectedGrades);
    },
    // Vaciar las dos listas nunca consulta a ClassLink, y es por eso la única
    // acción que sigue disponible con la fuente degradada.
    clearFilter() {
      this.save([], []);
    },
    async save(orgIds, grades) {
      if (!this.tenant) return;
      this.saving = true;
      try {
        await rosterTenantsService.saveScope(this.tenant.id, { orgIds, grades });
        this.snackbarSuccess(this.$t('classlink.scope.saved'));
        this.confirmOpen = false;
        this.$emit('saved');
        await this.load();
      } catch (error) {
        this.snackbarError(this.saveErrorMessage(error));
      } finally {
        this.saving = false;
      }
    },
    // 503 = el backend consultó ClassLink para validar los sourcedId y no está
    // disponible; 400 = alguno de los sourcedId ya no existe en el roster.
    saveErrorMessage(error) {
      const status = apiStatus(error);
      if (status === 503) {
        return this.$t('classlink.scope.unavailable_live');
      }
      if (status === 400) {
        return apiErrorMessage(error, this.$t('classlink.scope.invalid_orgs'));
      }
      return apiErrorMessage(error, this.$t('classlink.scope.save_error'));
    },
  },
};
</script>
